import {
  Dispatch, SetStateAction,
  useEffect, useRef,
} from 'react';
import { usePathname } from 'next/navigation';
import { buildSearchParams, buildObjectFromSearchParams } from '@utils/data-fetching/searchParams';
import { dequal } from 'dequal';
import { InfinitePageType } from '@type/swr';

/** Props */
interface UseAppHistoryAndURLProps {
  pageInView?: string | number,
  paginationType?: InfinitePageType,
  searchParams?: object,
  setSearchParams?: Dispatch<SetStateAction<object>>,
  preserveNumericLastSegment?: boolean,
}

/**
 * @description Hook to handle the history and URL updates for in the 'app' folder.
 * - `pageInView`: The current page in view.
 * - `paginationType`: The type of pagination used. Default is 'page'.
 * - `searchParams`: The search parameters to be added to the URL.
 * - `setSearchParams`: Function to set the search parameters.
 */
export const useAppHistoryAndURL = (props: UseAppHistoryAndURLProps) => {
  // Destructure props
  const {
    pageInView,
    paginationType = 'page',
    searchParams,
    setSearchParams,
    preserveNumericLastSegment = false,
  } = props;

  // Handle the pathname
  // Store the pathname in a ref in case the pathname changes when a modal is opened.
  const pathname = usePathname();
  const initialPathname = useRef<string>(pathname ?? '');

  // Keys this hook has ever managed. Used to preserve query params it doesn't own
  // (e.g. `?edit=1` set by SidebarNavigationContext) while still stripping
  // previously-managed keys when the caller clears them.
  const everManagedKeys = useRef<Set<string>>(new Set());


  // Update history and URL
  // *************************
  useEffect(() => {
    const urlSearchParams = searchParams ? buildSearchParams(searchParams) : '';

    // Use the current browser pathname as the base.
    // External code (e.g. SidebarNavigationContext) may have updated the URL via pushState;
    // using the stale initialPathname ref would overwrite those changes and cause infinite loops.
    initialPathname.current = window.location.pathname;
    const pathnameRef = initialPathname.current;
    const isNumberedPage = !Number.isNaN(Number(pathnameRef.substring(pathnameRef.lastIndexOf('/') + 1)));
    const cleanedPathname = isNumberedPage ? pathnameRef.substring(0, pathnameRef.lastIndexOf('/')) : pathnameRef;
    const basePathname = preserveNumericLastSegment ? pathnameRef : cleanedPathname;

    let adjustedPathname: string;
    if (paginationType === 'cursor') {
      adjustedPathname = pageInView ? `${basePathname}/${pageInView}` : basePathname;
    } else {
      adjustedPathname = pageInView && Number(pageInView) > 1 ? `${basePathname}/${pageInView}` : basePathname;
    }

    // Accumulate all keys we've ever seen in searchParams — these are the keys this hook owns.
    // Anything in the current URL outside this set was put there by something else
    // (e.g. `?edit=1` from SidebarNavigationContext) and must be preserved.
    Object.keys((searchParams as Record<string, unknown>) ?? {}).forEach((key) => {
      everManagedKeys.current.add(key);
    });
    const currentParams = new URLSearchParams(window.location.search);
    const preserved = new URLSearchParams();
    currentParams.forEach((value, key) => {
      if (!everManagedKeys.current.has(key)) preserved.append(key, value);
    });
    const preservedString = preserved.toString();
    let queryString = urlSearchParams;
    if (preservedString) {
      queryString = urlSearchParams
        ? `${urlSearchParams}&${preservedString}`
        : `?${preservedString}`;
    }

    const newUrl = `${adjustedPathname}${queryString}`;

    // Only push when the constructed URL differs from the current browser URL.
    // Other systems (e.g. SidebarNavigationContext) may have already updated the
    // URL via pushState; blindly overwriting would revert to the stale
    // initialPathname and cause infinite redirect loops on MC pages.
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (newUrl !== currentUrl) {
      window.history.pushState(null, '', newUrl);
    }
  }, [pageInView, paginationType, preserveNumericLastSegment, searchParams]);


  // Update the filters' object on history navigation
  // Chestia asta e netestată (eu nu am avut nevoie), ajustează pentru app folder dacă sunt probleme.
  // Este scrisă pentru compatibilitate cu app folder, dar repet, nu am testat-o.
  // **************************
  useEffect(() => {
    // Function to update the search params from the URL
    const updateParamsFromUrl = () => {
      if (setSearchParams) {
        const currentSearchParams = window.location.search;
        const currentParamsObj = buildObjectFromSearchParams(currentSearchParams) || {};
        // Check if the current params object is different from the previous one to avoid unnecessary updates
        setSearchParams((prevParamsObj) => {
          if (!dequal(prevParamsObj, currentParamsObj)) {
            return currentParamsObj;
          }
          return prevParamsObj;
        });
      }
    };

    // Listen for back/forward navigation
    window.addEventListener('popstate', updateParamsFromUrl);

    // Cleanup listener on unmounting
    return () => {
      window.removeEventListener('popstate', updateParamsFromUrl);
    };
  }, [setSearchParams]);
};
